import api from '@/config/api'
import vuex from '@/store'
import catchErrors from './catchErrors'

const prepareStation = (station) => station || vuex.getters.currentStation?.station || ''

// Several people edit a sequence at once, and each change reaches the others as a
// realtime event that reloads these. A cached response would hand back the state
// from before that change.
const live = { cache: { ignoreCache: true } }

export default {
  getSequencesList(station, params = {}) {
    station = prepareStation(station)
    return catchErrors.get(`/v2/newsdesk/stations/${station}/sequences`, { params, ...live })
  },
  createSequence(station, payload) {
    station = prepareStation(station)
    return catchErrors.post(`/v2/newsdesk/stations/${station}/sequences`, payload)
  },
  getSequence(id) {
    return api.get(`/v2/newsdesk/sequences/${id}`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  clearSequenceCache(id) {
    return api.post(`/v2/newsdesk/sequences/${id}/cache-clear`)
  },
  updateSequence(id, payload) {
    return api.patch(`/v2/newsdesk/sequences/${id}`, payload)
  },
  duplicateSequence(id) {
    return api.post(`/v2/newsdesk/sequences/${id}/duplicate`)
  },
  removeSequence(id) {
    return catchErrors.delete(`/v2/newsdesk/sequences/${id}`)
  },

  // items
  getSequenceItemsList(sequenceId, { params = {} } = {}) {
    return catchErrors.get(`/v2/newsdesk/sequences/${sequenceId}/items`, { params, ...live })
  },
  addSequenceItem(sequenceId, payload) {
    return catchErrors.post(`/v2/newsdesk/sequences/${sequenceId}/items`, payload)
  },
  getSequenceItem(itemId) {
    return api.get(`/v2/newsdesk/sequence-items/${itemId}`, live)
  },
  updateSequenceItem(itemId, payload) {
    return api.patch(`/v2/newsdesk/sequence-items/${itemId}`, payload)
  },
  removeSequenceItem(itemId) {
    return catchErrors.delete(`/v2/newsdesk/sequence-items/${itemId}`)
  },
  reorderSequenceItems(sequenceId, payload) {
    return catchErrors.post(`/v2/newsdesk/sequences/${sequenceId}/items/reorder`, payload)
  },
  /** `item_ids` is the target segment's (or unassigned's) whole order after the move. */
  moveSequenceItems(sequenceId, payload) {
    return catchErrors.post(`/v2/newsdesk/sequences/${sequenceId}/items/move`, payload)
  },
  moveSequenceItem(itemId, { sequenceId, segmentId, position }) {
    return catchErrors.patch(`/v2/newsdesk/sequence-items/${itemId}/move`, {
      sequence_id: sequenceId,
      segment_id: segmentId,
      sort_order_in_segment: segmentId ? position : 10000,
      sort_order: !segmentId ? position : 10000
    })
  },
  duplicateSequenceItem(itemId, { sequenceId, segmentId, position }) {
    return catchErrors.post(`/v2/newsdesk/sequence-items/${itemId}/duplicate`, {
      sequence_id: sequenceId,
      segment_id: segmentId,
      sort_order_in_segment: segmentId ? position : 10000,
      sort_order: !segmentId ? position : 10000
    })
  },
  overrideSequenceItem(itemId, payload) {
    return catchErrors.post(`/v2/newsdesk/sequence-items/${itemId}/override`, payload)
  },
  prepareSequenceItemDeliverable(itemId, params) {
    return api.get(`/v2/newsdesk/sequence-items/${itemId}/prepare-deliverable`, {
      params
    })
  },

  // segments
  getSequenceSegmentsList(sequenceId, { params = {} } = {}) {
    return catchErrors.get(`/v2/newsdesk/sequences/${sequenceId}/segments`, { params, ...live })
  },
  addSequenceSegment(sequenceId, payload) {
    return catchErrors.post(`/v2/newsdesk/sequences/${sequenceId}/segments`, payload)
  },
  getSequenceSegment(segmentId) {
    // Port fix: NPG called api.patch here, turning every segment read into a write.
    return api.get(`/v2/newsdesk/sequence-segments/${segmentId}`, live)
  },
  updateSequenceSegment(segmentId, payload) {
    return api.patch(`/v2/newsdesk/sequence-segments/${segmentId}`, payload)
  },
  removeSequenceSegment(segmentId) {
    return catchErrors.delete(`/v2/newsdesk/sequence-segments/${segmentId}`)
  },
  reorderSequenceSegmentItems(segmentId, payload) {
    return catchErrors.post(`/v2/newsdesk/sequence-segments/${segmentId}/reorder`, payload)
  },
  reorderSequenceSegments(sequenceId, payload) {
    return catchErrors.post(`/v2/newsdesk/sequences/${sequenceId}/segments/reorder`, payload)
  },
  lockSequenceSegment(segmentId) {
    return api.post(`/v2/newsdesk/sequence-segments/${segmentId}/lock`)
  },
  unlockSequenceSegment(segmentId) {
    return api.post(`/v2/newsdesk/sequence-segments/${segmentId}/unlock`)
  },

  // distribution targets
  getSequenceDistributionTargetsList(sequenceId, { params = {} } = {}) {
    return catchErrors.get(`/v2/newsdesk/sequences/${sequenceId}/distribution-targets`, { params, ...live })
  },
  addSequenceDistributionTarget(sequenceId, payload) {
    return catchErrors.post(`/v2/newsdesk/sequences/${sequenceId}/distribution-targets`, payload)
  },
  getSequenceDistributionTarget(targetId) {
    return api.get(`/v2/newsdesk/sequence-distribution-targets/${targetId}`, live)
  },
  updateSequenceDistributionTarget(targetId, payload) {
    return api.patch(`/v2/newsdesk/sequence-distribution-targets/${targetId}`, payload)
  },
  removeSequenceDistributionTarget(targetId) {
    return catchErrors.delete(`/v2/newsdesk/sequence-distribution-targets/${targetId}`)
  },

  // formats
  getSequenceFormatsList({ station } = {}) {
    station = prepareStation(station)
    return catchErrors.get(`/v2/newsdesk/stations/${station}/sequence-formats`)
  },
  addSequenceFormat(station, payload) {
    station = prepareStation(station)
    return catchErrors.post(`/v2/newsdesk/stations/${station}/sequence-formats`, payload)
  },
  getSequenceFormat(formatId) {
    return api.get(`/v2/newsdesk/sequence-formats/${formatId}`)
  },
  updateSequenceFormat(formatId, payload) {
    return api.patch(`/v2/newsdesk/sequence-formats/${formatId}`, payload)
  },
  removeSequenceFormat(formatId) {
    return catchErrors.delete(`/v2/newsdesk/sequence-formats/${formatId}`)
  },
  instantiateSequenceFromTemplate(formatId, payload = {}) {
    return catchErrors.post(`/v2/newsdesk/sequence-formats/${formatId}/instantiate`, payload)
  },
  instantiateSequenceFromTemplateBulk(formatId, payload = {}) {
    return catchErrors.post(`/v2/newsdesk/sequence-formats/${formatId}/instantiate-bulk`, payload)
  },

  // timing & lock & versions
  lockSequence(sequenceId) {
    return catchErrors.post(`/v2/newsdesk/sequences/${sequenceId}/lock`)
  },
  unlockSequence(sequenceId) {
    return catchErrors.post(`/v2/newsdesk/sequences/${sequenceId}/unlock`)
  },
  getSequenceTiming(sequenceId) {
    return catchErrors.get(`/v2/newsdesk/sequences/${sequenceId}/timing`, live)
  },
  recalculateSequenceTiming(sequenceId) {
    return catchErrors.post(`/v2/newsdesk/sequences/${sequenceId}/recalculate-timing`)
  },
  createSequenceVersion(sequenceId, payload = {}) {
    return catchErrors.post(`/v2/newsdesk/sequences/${sequenceId}/version`, payload)
  },
  getSequenceVersions(sequenceId) {
    return catchErrors.get(`/v2/newsdesk/sequences/${sequenceId}/versions`, live)
  },
  getSequenceChanges(sequenceId, params = {}) {
    return catchErrors.get(`/v2/newsdesk/sequences/${sequenceId}/changes`, {
      params,
      ...live
    })
  },

  // readiness
  getSequenceReadiness(sequenceId) {
    return catchErrors.get(`/v2/newsdesk/sequences/${sequenceId}/readiness`, live)
  },
  getSequencesListReadiness(station, payload = {}) {
    station = prepareStation(station)
    return catchErrors.post(`/v2/newsdesk/stations/${station}/sequences/readiness`, payload)
  },

  // export
  exportSequenceToTarget(sequenceId, targetId) {
    return api.post(`/v2/newsdesk/sequences/${sequenceId}/export`, {
      target_id: targetId
    })
  },
  exportSequenceAll(sequenceId) {
    return api.post(`/v2/newsdesk/sequences/${sequenceId}/export-all`)
  }
}
